import { Trash } from "@procore/core-icons";
import {
   Button,
   H3,
   Modal,
   Spinner,
   Tooltip,
   Typography,
   useI18nContext,
   ZIndexProvider,
} from "@procore/core-react";
import React, { useEffect, useState } from "react";
import { useToastAlertContext } from "@procore/toast-alert";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import { DateUtils } from "@/lib/utils/date";
import type { ConfirmDeleteModalAssignmentsPropTypes } from "@/react/components/assignment-list/assignment-list-prop-types";

export const getResolvedTooltipText = (
   hasCompletedAssignment: boolean,
   hasInProgressAssignment: boolean,
): string => {
   const baseKey = "views.company.workforce_planning.assignment.bulk.delete.";

   switch (true) {
      case hasCompletedAssignment && hasInProgressAssignment:
         return `${baseKey}completed_or_in_progress_assignment_tooltip`;

      case hasCompletedAssignment:
         return `${baseKey}completed_assignment_tooltip`;

      case hasInProgressAssignment:
         return `${baseKey}in_progress_assignment_tooltip`;

      default:
         return `${baseKey}completed_assignment_tooltip`;
   }
};

export const ConfirmDeleteAssignmentModal = (props: ConfirmDeleteModalAssignmentsPropTypes) => {
   const {
      tableApi,
      headerText,
      deleteText,
      successText,
      highlightedText,
      errorText,
      selectedRows = [],
      selectionInProgress,
      setManuallySelectedRowData,
   } = props;
   const I18n = useI18nContext();
   const [open, setOpen] = useState(false);
   const [loading, setLoading] = useState(false);
   const [hasCompletedAssignment, setHasCompletedAssignment] = useState(false);
   const [hasInProgressAssignment, setHasInProgressAssignment] = useState(false);

   const { showToast } = useToastAlertContext();

   useEffect(() => {
      if (selectedRows.length > 0) {
         const current_day = DateUtils.getDetachedDay(new Date());

         let completed = false;
         let inProgress = false;

         selectedRows.forEach((assignment) => {
            const { start_day, end_day } = assignment;
            // Check in-progress first
            if (current_day > start_day && current_day < end_day) {
               inProgress = true;
            } else if (current_day > end_day) {
               completed = true;
            }
         });
         setHasCompletedAssignment(completed);
         setHasInProgressAssignment(inProgress);
      }
   }, [selectedRows]);

   const handleDelete = async () => {
      try {
         setLoading(true);
         const ids = selectedRows?.map((x: any) => x.id);
         await Assignment2Store.batchDelete({ ids }).stream;
         tableApi?.deselectAll();
         tableApi?.refreshServerSide({ purge: true });
         showToast.success(I18n.t(successText));
         setManuallySelectedRowData([]);
      } catch (error) {
         showToast.error(I18n.t(errorText));
      } finally {
         setLoading(false);
         setOpen(false);
      }
   };

   const isDeleteDisabled = hasCompletedAssignment || hasInProgressAssignment;

   return (
      <>
         <ZIndexProvider>
            <Tooltip
               overlay={I18n.t(
                  getResolvedTooltipText(hasCompletedAssignment, hasInProgressAssignment),
               )}
               placement="left"
               trigger={isDeleteDisabled ? "hover" : "none"}
            >
               <div>
                  <Button
                     icon={<Trash />}
                     onClick={() => setOpen(true)}
                     variant="tertiary"
                     data-testid="trash-btn"
                     disabled={isDeleteDisabled || selectionInProgress}
                     aria-label={I18n.t("views.company.workforce_planning.delete")}
                  />
               </div>
            </Tooltip>
         </ZIndexProvider>

         <Modal open={open} onClose={() => setOpen(false)} width="md">
            <Modal.Header>{I18n.t(headerText)}</Modal.Header>
            <Spinner loading={loading}>
               <Modal.Body>
                  <Typography>
                     {I18n.t(deleteText)}
                     <H3>{I18n.t(highlightedText)}</H3>
                  </Typography>
               </Modal.Body>
               <Modal.Footer>
                  <Modal.FooterButtons>
                     <Button variant="secondary" onClick={() => setOpen(false)}>
                        {I18n.t("views.company.workforce_planning.cancel")}
                     </Button>
                     <Button variant="primary" onClick={handleDelete}>
                        {I18n.t("views.company.workforce_planning.delete")}
                     </Button>
                  </Modal.FooterButtons>
               </Modal.Footer>
            </Spinner>
         </Modal>
      </>
   );
};
